/**
 * "Is this element about to be on screen?", as one boolean that only ever goes
 * from false to true.
 *
 * It exists so a heavy piece of the landing page can be a lazy chunk that is
 * fetched just before the reader reaches it, rather than on load. The default
 * margin is deliberately generous: the chunk request, the parse and the first
 * paint all have to fit inside the time it takes to scroll the last stretch, and
 * a hole that fills in is worse than a still that starts moving.
 *
 * It latches on purpose. Unmounting a WebGL context or a corridor when the
 * reader scrolls back past it would trade a small amount of memory for a
 * visible rebuild, and the components already stop drawing frames when they
 * leave the viewport.
 */

import { useEffect, useRef, useState } from 'react';

export function useNearViewport<T extends Element>(
  rootMargin = '400px',
): [React.RefObject<T | null>, boolean] {
  const ref = useRef<T>(null);
  const [near, setNear] = useState(false);

  useEffect(() => {
    if (near) return;
    const node = ref.current;
    // No element, or a browser without the observer: mount it rather than
    // withhold it. Failing open is right here, because the alternative is a
    // page that never shows the thing at all.
    if (!node || typeof IntersectionObserver === 'undefined') {
      setNear(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setNear(true);
          observer.disconnect();
        }
      },
      { rootMargin },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [near, rootMargin]);

  return [ref, near];
}
